<script lang="ts">
	import { onMount } from 'svelte'
	import type { AvatarType, FileValidationOptions, UploadPayload } from './types'
	import { readFileAsDataURL, validateFile, DEFAULT_VALIDATION } from './utils'
	import Button, { ButtonSize, ButtonType } from '../Button.svelte'
	import Avatar from '../Avatar.svelte'
	import { showToast } from '@repo/commons/utils'
	import { toastsStore } from '@repo/commons/store'
	import { ToastType } from '../Toast.svelte'
	import type { ToastModel } from '$lib_commons/models/toast-model.ts'

	interface Props {
		selectedAvatar?: AvatarType
		avatars: AvatarType[]
		uploadButtonText: string
		validation?: FileValidationOptions
		isLoading?: boolean
		onSelect?: (avatar: AvatarType) => void
		onUpload?: (payload: UploadPayload) => void
		onRemove?: (index: number) => void
	}

	let {
		uploadButtonText,
		avatars = $bindable([]),
		selectedAvatar = $bindable(null),
		validation = DEFAULT_VALIDATION,
		isLoading = false,
		onSelect,
		onUpload,
		onRemove
	}: Props = $props()

	let fileInput: HTMLInputElement | null = null

	const select = (index: number) => {
		selectedAvatar = avatars[index]
		onSelect?.(selectedAvatar)
	}

	const openPicker = () => {
		fileInput?.click()
	}

	const handleFile = async (file: File | null | undefined) => {
		if (!file) return

		const validated = validateFile(file, validation)
		if (!validated.ok) {
			showToast(toastsStore, {
				heading: 'Invalid file',
				message: validated.error || 'Please select a valid file',
				type: ToastType.ERROR
			} as ToastModel)
			return
		}

		try {
			const dataUrl = await readFileAsDataURL(file)
			const index = avatars.findIndex((avatar) => avatar.src === selectedAvatar?.src)

			onUpload?.({
				index,
				file,
				dataUrl
			})
		} catch (error) {
			console.error('File upload error:', error)
			showToast(toastsStore, {
				heading: 'Upload error',
				message: 'Failed to process the file',
				type: ToastType.ERROR
			} as ToastModel)
		}
	}

	const onFileChange = (e: Event) => {
		const input = e.target as HTMLInputElement | null
		const file = input?.files?.[0] ?? null

		handleFile(file)

		if (input) input.value = ''
	}

	const onDrop = (e: DragEvent) => {
		e.preventDefault()
		const file = e.dataTransfer?.files?.[0] ?? null
		handleFile(file)
	}

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault()
	}

	const onKey = (e: KeyboardEvent, idx: number) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			select(idx)
			return
		}
		if (e.key === 'ArrowRight') {
			e.preventDefault()
			const next = (idx + 1) % avatars.length
			select(next)
			focusAvatar(next)
			return
		}

		if (e.key === 'ArrowLeft') {
			e.preventDefault()
			const prev = (idx - 1 + avatars.length) % avatars.length
			select(prev)
			focusAvatar(prev)
			return
		}

		if (e.key === 'Delete' || e.key === 'Backspace') {
			e.preventDefault()
			onRemove?.(idx)
		}
	}

	const focusAvatar = (i: number) => {
		const el = document.getElementById(`avatar-${i}`)
		el?.focus()
	}

	onMount(() => {
		avatars = avatars.map((avatar) => ({ ...avatar, src: avatar.src ?? '' }))
	})
</script>

<div class="wrap">
	<div class="upload">
		<Button
			onclick={openPicker}
			buttonText={uploadButtonText}
			buttonSize={ButtonSize.SMALL}
			buttonType={ButtonType.TERTIARY}
			extraProps="text-underline-offset: 4px"
			disabled={isLoading}
		/>
		<input
			type="file"
			bind:this={fileInput}
			onchange={onFileChange}
			class="hidden_file_input"
			accept={validation.allowedTypes?.join(',')}
		/>
	</div>

	<div class="list" tabindex="-1" role="listbox" ondrop={onDrop} ondragover={handleDragOver}>
		{#each avatars as avatar, index (avatar.id)}
			{@const { src, label, variant } = avatar}
			{@const isSelected = selectedAvatar?.id === avatar?.id}

			<div
				tabindex="0"
				role="option"
				title={label}
				id={`avatar-${index}`}
				onclick={() => select(index)}
				aria-selected={isSelected}
				onkeydown={(e) => onKey(e, index)}
				class="avatar"
				class:selected={isSelected}
			>
				<Avatar
					imageSrc={src || ''}
					imageWithGradientBg={!!src}
					fallback={variant || label || ''}
					className="avatar_wrapper"
					isLoading={isLoading && isSelected}
				/>
			</div>
		{/each}
	</div>
</div>

<style>
	.hidden_file_input {
		display: none;
	}
	.wrap {
		@apply flex items-center gap-4;
	}

	.upload {
		@apply flex flex-col gap-1.5;
	}

	.list {
		@apply flex gap-4 rounded-lg;
	}

	.avatar {
		@apply w-[100px] h-[100px] rounded-full overflow-hidden
        border border-[#003A661A] cursor-pointer
        transition-[transform,border-color] duration-150;
	}

	.avatar.selected {
		@apply border-4 border-[#007ACC] -translate-y-[3px];
	}

	.avatar:focus-visible {
		@apply outline-none;
	}

	:global(.avatar_wrapper.fallback-wrapper) {
		@apply max-w-full;
	}
</style>
